/**
 * Push Service for true Web Push notifications.
 *
 * Works with the push hub (a small Cloudflare Worker, see worker/) that stores
 * push subscriptions and delivers notifications through the platform push
 * services (Apple / Google). Unlike notificationService.js, which can only
 * alert while the page is open, these notifications arrive when the phone is
 * locked and the app is closed.
 *
 * On iPhone this requires iOS 16.4+ and the app opened from its Home Screen
 * icon (which Dr. Grant already has installed).
 */

const HUB_URL = (process.env.REACT_APP_PUSH_HUB_URL || '').replace(/\/+$/, '');
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

/**
 * Convert a URL-safe base64 VAPID key to the Uint8Array form
 * required by pushManager.subscribe().
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

class PushService {
  constructor() {
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  /**
   * The hub URL and VAPID public key both come from build-time env vars.
   * Without them the lock-screen notification UI stays hidden.
   */
  isConfigured() {
    return Boolean(HUB_URL && VAPID_PUBLIC_KEY);
  }

  getHubUrl() {
    return HUB_URL;
  }

  isSupported() {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  /**
   * True when running as an installed app (Home Screen icon / PWA window).
   * iOS only allows push subscriptions in this mode.
   */
  isStandalone() {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    );
  }

  /**
   * iPhone/iPad in a regular Safari tab cannot subscribe to push;
   * the app must be opened from the Home Screen icon first.
   */
  needsHomeScreenInstall() {
    return this.isIOS && !this.isStandalone();
  }

  async getSubscription() {
    if (!this.isSupported()) return null;
    try {
      const registration = await navigator.serviceWorker.ready;
      return await registration.pushManager.getSubscription();
    } catch (error) {
      console.warn('Could not read push subscription:', error);
      return null;
    }
  }

  async isSubscribed() {
    const subscription = await this.getSubscription();
    return Boolean(subscription);
  }

  /**
   * Subscribe this device to lock-screen push notifications.
   * Must be called from a user gesture (button tap) so the iOS
   * permission prompt is allowed to appear.
   */
  async subscribe(label = 'Dr. Grant device') {
    if (!this.isConfigured()) {
      return { ok: false, reason: 'not-configured' };
    }
    if (!this.isSupported()) {
      return { ok: false, reason: 'not-supported' };
    }
    if (this.needsHomeScreenInstall()) {
      return { ok: false, reason: 'needs-home-screen' };
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { ok: false, reason: 'permission-denied' };
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
        });
      }

      const registered = await this.registerWithHub(subscription, label);
      if (!registered) {
        return { ok: false, reason: 'hub-unreachable' };
      }

      console.log('Push subscription active:', subscription.endpoint);
      return { ok: true };
    } catch (error) {
      console.error('Push subscribe failed:', error);
      return { ok: false, reason: 'subscribe-failed', error };
    }
  }

  /**
   * Store/refresh the subscription on the hub. Safe to call repeatedly;
   * the hub upserts by endpoint.
   */
  async registerWithHub(subscription, label = '') {
    try {
      const response = await fetch(`${HUB_URL}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          label,
          userAgent: navigator.userAgent
        })
      });
      return response.ok;
    } catch (error) {
      console.error('Could not register subscription with push hub:', error);
      return false;
    }
  }

  /**
   * Re-sync an existing subscription with the hub on app start, so the hub
   * always has the current endpoint even if it changed since last launch.
   */
  async syncRegistration() {
    if (!this.isConfigured()) return;
    try {
      const subscription = await this.getSubscription();
      if (subscription && Notification.permission === 'granted') {
        await this.registerWithHub(subscription);
      }
    } catch (error) {
      console.warn('Push registration sync failed:', error);
    }
  }

  async unsubscribe() {
    const subscription = await this.getSubscription();
    if (!subscription) return true;

    try {
      await fetch(`${HUB_URL}/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: subscription.endpoint })
      });
    } catch (error) {
      console.warn('Hub unsubscribe failed (continuing):', error);
    }

    try {
      await subscription.unsubscribe();
      return true;
    } catch (error) {
      console.error('Push unsubscribe failed:', error);
      return false;
    }
  }

  /**
   * Ask the hub to send a real push to this device after a short delay,
   * so Dr. Grant can lock her phone and confirm delivery end to end.
   */
  async sendTestPush(delaySeconds = 5) {
    const subscription = await this.getSubscription();
    if (!subscription) {
      return { ok: false, reason: 'not-subscribed' };
    }
    try {
      const response = await fetch(`${HUB_URL}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          delaySeconds
        })
      });
      return { ok: response.ok };
    } catch (error) {
      console.error('Test push failed:', error);
      return { ok: false, reason: 'hub-unreachable' };
    }
  }

  /**
   * Tell the hub a student just joined the queue. The hub verifies the
   * record against Airtable and pushes to every registered device.
   * Fire-and-forget: never blocks or fails the queue join itself.
   */
  notifyQueueJoin(recordId) {
    if (!this.isConfigured() || !recordId) return;
    try {
      fetch(`${HUB_URL}/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recordId }),
        keepalive: true
      }).catch((error) => {
        console.warn('Push hub notify failed (cron backstop will cover it):', error);
      });
    } catch (error) {
      console.warn('Push hub notify failed:', error);
    }
  }
}

export const pushService = new PushService();
